/**
 * Identidad visible del usuario en sesión: nombre e iniciales del avatar.
 *
 * ── Por qué existe ──────────────────────────────────────────────────────────
 * Cada cabecera calculaba las iniciales por su cuenta con la misma regla:
 * «si hay `first_name` Y `last_name`, sus primeras letras; si no, "DU"». Eso
 * fallaba de tres maneras y las tres terminaban en las iniciales inventadas
 * «DU» (de «Default User»):
 *
 *   1. `/auth/register` y `/login` responden `firstName` / `lastName` (camel),
 *      pero las cabeceras sólo leían `first_name` / `last_name` (snake).
 *   2. Quien tiene un solo nombre (alta con Google sin apellido, invitaciones)
 *      nunca cumplía la condición «los dos».
 *   3. Quien no tiene nombre cargado no caía al correo.
 *
 * Aquí vive la única regla. Nunca devuelve iniciales inventadas: sin nombre y
 * sin correo devuelve "" y quien pinta muestra un icono neutro.
 */

export interface UserNameSource {
  first_name?: unknown;
  last_name?: unknown;
  firstName?: unknown;
  lastName?: unknown;
  name?: unknown;
  displayName?: unknown;
  display_name?: unknown;
  full_name?: unknown;
  fullName?: unknown;
  email?: unknown;
  username?: unknown;
}

const clean = (value: unknown): string =>
  typeof value === "string" ? value.replace(/\s+/g, " ").trim() : "";

/** Caracteres «de texto» (letras o dígitos) de una palabra, ya compuestos. */
const textChars = (word: string): string[] =>
  Array.from(word.normalize("NFC")).filter((c) => /[\p{L}\p{N}]/u.test(c));

/** Primer carácter de texto de una palabra, en mayúscula. */
const firstChar = (word: string): string => {
  const chars = textChars(word);
  return chars.length > 0 ? chars[0].toLocaleUpperCase() : "";
};

/** Las dos primeras letras de una sola palabra («Ana» → «AN»). */
const firstTwoChars = (word: string): string => textChars(word).slice(0, 2).join("").toLocaleUpperCase();

const words = (text: string): string[] => text.split(" ").filter((w) => firstChar(w) !== "");

/**
 * Nombre y apellido tal como vengan: snake (`/user`), camel (`/login`,
 * `/auth/register`) o un nombre completo en un solo campo (`name`,
 * `displayName`), que se parte en la primera palabra y el resto.
 */
export function resolveUserNames(user?: UserNameSource | null): { firstName: string; lastName: string } {
  if (!user || typeof user !== "object") return { firstName: "", lastName: "" };

  const firstName = clean(user.first_name) || clean(user.firstName);
  const lastName = clean(user.last_name) || clean(user.lastName);
  if (firstName || lastName) return { firstName, lastName };

  const full =
    clean(user.name) ||
    clean(user.displayName) ||
    clean(user.display_name) ||
    clean(user.full_name) ||
    clean(user.fullName);
  // Un «nombre» que en realidad es el correo no es un nombre.
  if (!full || full.includes("@")) return { firstName: "", lastName: "" };

  const [first, ...rest] = full.split(" ");
  return { firstName: first, lastName: rest.join(" ") };
}

/** Parte local del correo («ana.lopez@x.com» → «ana.lopez»). */
const emailLocalPart = (value: unknown): string => clean(value).split("@")[0] ?? "";

const initialsFromHandle = (handle: string): string => {
  const parts = handle.split(/[._\-+\s]+/).filter((p) => firstChar(p) !== "");
  if (parts.length >= 2) return `${firstChar(parts[0])}${firstChar(parts[1])}`;
  return parts.length === 1 ? firstTwoChars(parts[0]) : "";
};

/**
 * Iniciales del avatar (1–2 caracteres) o "" si no hay de dónde sacarlas.
 *
 *   nombre + apellido       → primera letra de cada uno       («Ana López» → «AL»)
 *   un solo campo, 2+ pal.  → primeras letras de las dos 1.as («María José» → «MJ»)
 *   una sola palabra        → sus dos primeras letras         («Ana» → «AN»)
 *   sin nombre              → lo mismo sobre el correo        («ana.lopez@…» → «AL»)
 *   sin nada                → ""  (quien pinta muestra un icono, nunca «DU»)
 */
export function getUserInitials(user?: UserNameSource | null): string {
  const { firstName, lastName } = resolveUserNames(user);
  const firstWords = words(firstName);
  const lastWords = words(lastName);

  if (firstWords.length > 0 && lastWords.length > 0) {
    return `${firstChar(firstWords[0])}${firstChar(lastWords[0])}`;
  }
  const only = firstWords.length > 0 ? firstWords : lastWords;
  if (only.length >= 2) return `${firstChar(only[0])}${firstChar(only[1])}`;
  if (only.length === 1) return firstTwoChars(only[0]);

  if (!user || typeof user !== "object") return "";
  return initialsFromHandle(emailLocalPart(user.email)) || initialsFromHandle(emailLocalPart(user.username));
}

/**
 * Nombre para mostrar: «Nombre Apellido», o el que haya; si no hay nombre, el
 * usuario o el correo. "" si no hay nada (quien pinta pone su texto genérico).
 */
export function getUserDisplayName(user?: UserNameSource | null): string {
  const { firstName, lastName } = resolveUserNames(user);
  const full = `${firstName} ${lastName}`.trim();
  if (full) return full;
  if (!user || typeof user !== "object") return "";
  return clean(user.username) || clean(user.email);
}

/**
 * Deja al usuario del store con `first_name` / `last_name` siempre presentes
 * cuando el dato existe en cualquiera de sus formas. Es aditivo: no borra ni
 * pisa campos, así que quien lee `firstName` sigue funcionando.
 */
export function withUserNameFields<T>(user: T): T {
  if (!user || typeof user !== "object" || Array.isArray(user)) return user;
  const source = user as UserNameSource;
  if (clean(source.first_name) && clean(source.last_name)) return user;

  const { firstName, lastName } = resolveUserNames(source);
  if (!firstName && !lastName) return user;

  const next: Record<string, unknown> = { ...(user as Record<string, unknown>) };
  let changed = false;
  if (!clean(source.first_name) && firstName) {
    next.first_name = firstName;
    changed = true;
  }
  if (!clean(source.last_name) && lastName) {
    next.last_name = lastName;
    changed = true;
  }
  return changed ? (next as T) : user;
}
